import { ArchiveIcon, ChevronRightIcon } from "lucide-react"
import type { MouseEvent, PointerEventHandler, ReactNode } from "react"
import { Link } from "react-router"
import { Badge } from "@/primitives/badge.tsx"
import { Button } from "@/primitives/button.tsx"
import { cn } from "@/primitives/utils"
import { Key } from "./key.tsx"
import { useStickyTop } from "./page-chrome.tsx"
import type { TicketCounts } from "./stat.ts"
import { TicketProgress } from "./stat.tsx"
import { opensPeek } from "./ticket-peek.ts"

/*
 * The head of a group of rows in a list, such as one Project's tickets on My tickets: a sunken
 * band that sticks under the page's chrome while its rows scroll beneath, so the Member always
 * knows whose rows they are reading. It collapses its group, and the page keeps which groups are
 * collapsed (per viewer, in localStorage), from its chevron or a click on its empty part. It draws
 * only the hairline under it: the one above is the toolbar's, or the previous group's last row's,
 * and it would double if it drew its own.
 */

/**
 * `glyph` goes before the `label` (a Project's tile, an Epic's bolt), `labelKey` is a key shown
 * before the label as part of it (an Epic's), `href` makes the label a link, which with `onPeek`
 * opens the Ticket peek on a plain click (`opensPeek`) and leaves any other to the link, as a
 * ticket's row does. `secondary` is a key shown after it, then the `count` of rows. At the end,
 * `trailing` (an Epic's chips), then `progress`: how its tickets stand, as `TicketProgress` shows
 * them, in one column down the list and shown even with none to count. `badges` say what the
 * group's owner is, as "Archived" does for an archived Project's tickets, in the same outline Badge
 * the Projects list gives its row. `rows` is the id of the element holding the group's rows, which
 * the chevron says it shows and hides.
 *
 * `drag` makes the group draggable, as an open Epic is in the Epic order: the pointer drags it from
 * anywhere on the band, and the keyboard from its `grip`, which stands in the band's left padding.
 */
export function GroupHeader({
  collapsed,
  onToggle,
  glyph,
  label,
  secondary,
  count,
  progress,
  badges = [],
  href,
  labelKey,
  onPeek,
  trailing,
  drag,
  rows,
}: {
  collapsed: boolean
  onToggle: () => void
  glyph: ReactNode
  label: string
  secondary?: string
  count: number
  progress?: TicketCounts
  badges?: readonly "archived"[]
  href?: string
  labelKey?: string
  onPeek?: () => void
  trailing?: ReactNode
  drag?: { grip: ReactNode; onPointerDown: PointerEventHandler<HTMLElement> | undefined }
  rows?: string
}) {
  const top = useStickyTop()
  const named = (
    <>
      {labelKey && <Key value={labelKey} />}
      <span className="truncate text-foreground">{label}</span>
    </>
  )
  return (
    <div
      role="heading"
      aria-level={2}
      onPointerDown={drag?.onPointerDown}
      // The chevron is the keyboard's way to fold; a click anywhere else but a link is the pointer's.
      onClick={(event) => {
        if ((event.target as Element).closest("a, button")) return
        onToggle()
      }}
      className={cn(
        "group/row sticky z-10 flex h-9 items-center gap-2 border-b border-border-subtle bg-surface-sunken px-6 text-ui font-medium",
        drag ? "cursor-grab" : "cursor-pointer",
        top,
      )}
    >
      {drag?.grip}
      <Button
        variant="ghost"
        size="icon-xs"
        aria-expanded={!collapsed}
        aria-controls={rows}
        aria-label={labelKey ? `${labelKey} ${label}` : label}
        onClick={onToggle}
        // Open is the resting state here, so it doesn't take the selected fill an open menu does.
        className="-ml-1 aria-expanded:bg-transparent aria-expanded:text-muted-foreground aria-expanded:hover:bg-accent aria-expanded:hover:text-foreground"
      >
        <ChevronRightIcon
          className={cn("transition-transform duration-100 ease-snappy", !collapsed && "rotate-90")}
        />
      </Button>
      {glyph}
      {href ? (
        <Link
          to={href}
          {...(onPeek && {
            onClick: (event: MouseEvent) => {
              if (!opensPeek(event)) return
              event.preventDefault()
              onPeek()
            },
          })}
          className="flex min-w-0 items-center gap-2 rounded-sm outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
        >
          {named}
        </Link>
      ) : (
        <span className="flex min-w-0 items-center gap-2">{named}</span>
      )}
      {secondary && <Key value={secondary} />}
      <span className="text-xs font-normal text-subtle-foreground tabular-nums">{count}</span>
      {badges.map((badge) => (
        <Badge key={badge} variant="outline">
          <ArchiveIcon aria-hidden />
          Archived
        </Badge>
      ))}
      <span className="ml-auto flex min-w-0 shrink-0 items-center gap-1.5">
        {trailing}
        {progress && (
          <span className="ml-2 flex">
            <TicketProgress counts={progress} width={20} />
          </span>
        )}
      </span>
    </div>
  )
}
